import React from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Flex, Link, Text } from '@workwhile/ui'
import { isSameYear } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { UnpaidShift } from 'api/unpaidShifts'
import { AmountRender } from '../../UnpaidShiftsRenderers/AmountRender'
import { ShiftStatusRender } from '../../UnpaidShiftsRenderers/ShiftStatusRender'
import { TimelineRender } from '../../UnpaidShiftsRenderers/TimelineRender'
import { getLocalTimezone } from 'lib/time'

/**
 * Non-selectable columns go here. They're exported out to be used directly in the table.
 */

export const IDColumn: ColumnDef<UnpaidShift> = {
  header: 'ID',
  accessorKey: 'workId',
  minSize: 50,
  cell: ({ row }) => <Text>{row.original.workId}</Text>,
}

export const UserColumn: ColumnDef<UnpaidShift> = {
  header: 'User',
  accessorKey: 'userId',
  minSize: 150,
  cell: ({ row }) => {
    const userId = row.original.userId
    const userName = row.original.userName
    const displayName = userName || ''
    return (
      <Text>
        {displayName ? `${displayName} (` : ''}
        <Link to={`/users/${userId}`} target="_blank">
          {userId}
        </Link>
        {displayName ? ')' : ''}
      </Text>
    )
  },
}

export const AmountColumn: ColumnDef<UnpaidShift> = {
  header: 'Expected Earnings',
  accessorKey: 'amount',
  minSize: 120,
  cell: ({ row }) => <AmountRender value={row.original.amount} />,
}

export const ShiftColumn: ColumnDef<UnpaidShift> = {
  header: 'Shift',
  accessorKey: 'shiftId',
  minSize: 180,
  cell: ({ row }) => {
    const shiftId = row.original.shiftId
    const positionId = row.original.positionId
    const positionName = row.original.positionName
    const companyId = row.original.companyId
    const companyName = row.original.companyName

    return (
      <Flex flexDirection="column" gap={0.5}>
        <Text>
          Shift:{' '}
          <Link to={`/shifts/${shiftId}`} target="_blank">
            #{shiftId}
          </Link>
        </Text>
        {positionId && positionName ? (
          <Text fontSize="sm">
            Position: {positionName} (
            <Link
              to={`/position_editor?positionId=${positionId}`}
              target="_blank"
            >
              #{positionId}
            </Link>
            )
          </Text>
        ) : null}
        {companyId && companyName ? (
          <Text fontSize="sm">
            Company: {companyName} (
            <Link to={`/companies/${companyId}`} target="_blank">
              #{companyId}
            </Link>
            )
          </Text>
        ) : null}
      </Flex>
    )
  },
}

export const ShiftCompletedAtColumn: ColumnDef<UnpaidShift> = {
  header: 'Shift Completed At',
  accessorKey: 'shiftCompletedAt',
  minSize: 180,
  cell: ({ row }) => {
    const completedAt = row.original.shiftCompletedAt
    if (!completedAt) return <Text>-</Text>

    const isCurrentYear = isSameYear(completedAt, new Date())
    const timeZone = getLocalTimezone()
    const formatted = formatInTimeZone(
      completedAt,
      timeZone,
      isCurrentYear
        ? 'EEE MMM, dd, hh:mm a zz'
        : 'EEE MMM, dd, yyyy, hh:mm a zz'
    )
    return <Text>{formatted}</Text>
  },
}

export const ShiftStatusColumn: ColumnDef<UnpaidShift> = {
  header: 'Status',
  accessorKey: 'shiftCurrentStatus',
  minSize: 120,
  cell: ({ row }) => {
    const status = row.original.shiftCurrentStatus
    return <ShiftStatusRender value={status || ''} />
  },
}

export const TypeColumn: ColumnDef<UnpaidShift> = {
  header: 'Type',
  accessorKey: 'w2',
  minSize: 80,
  cell: ({ row }) => <Text>{row.original.w2 ? 'W2' : '1099'}</Text>,
}

export const TimelineColumn: ColumnDef<UnpaidShift> = {
  header: 'Timeline',
  accessorKey: 'statusTransitions',
  minSize: 250,
  cell: ({ row }) => {
    return <TimelineRender statusTransitions={row.original.statusTransitions} />
  },
}

export const COLUMNS = [
  {
    id: 'user',
    label: 'User',
    columnDef: UserColumn,
  },
  {
    id: 'amount',
    label: 'Expected Earnings',
    columnDef: AmountColumn,
  },
  {
    id: 'type',
    label: 'Type',
    columnDef: TypeColumn,
  },
  {
    id: 'shift',
    label: 'Shift',
    columnDef: ShiftColumn,
  },
  {
    id: 'shift-completed-at',
    label: 'Shift Completed At',
    columnDef: ShiftCompletedAtColumn,
  },
  {
    id: 'shift-status',
    label: 'Status',
    columnDef: ShiftStatusColumn,
  },

  {
    id: 'timeline',
    label: 'Timeline',
    columnDef: TimelineColumn,
  },
] as const

export type SelectableColumns = (typeof COLUMNS)[number]['id']
